/* eslint-disable no-console */
import {Divider} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import Loader from '../../../../../../components/Loader/Loader';
import EmpTable from '../../../../../../components/Table/EmpTable';
import {APITypeEnum} from '../../../../../../services/interface/tableAPIType';
import country from '../../../../../../services/modules/JSON/country';
import designation from '../../../../../../services/modules/JSON/designation';
import facilityType from '../../../../../../services/modules/JSON/facilityType';
import facilityDetailsDropdown from '../../../../../../services/modules/OM/facilityDetails';
import yearFilterArray from '../../../../../../shared/YearFilterArray';
import useCompanyStore from '../../../../../../store/companyStore/companyDetailsStore';
import {sortYearOptions} from '../../../../../orgManager/Utils/InfraStructureProfile/constant/sorYear';
import {IFacilityDetail} from '../../../../../orgManager/Utils/InfraStructureProfile/interface/commonI';
import FacilitySelect from '../../../../../orgManager/Utils/InfraStructureProfile/Utils/FacilitySelect';
import SelectedCountryContext from '../../../../orgFacility/Constants/SelectedCountryContext';

import efdbDatabaseDropdown from './../../../../../../services/modules/JSON/efdbDatabaseDropdown';

const EmployeeDetails: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchParam, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [efdbData, setEfdbData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const facilities = useCompanyStore.getState().facilityDetails;
  const [facilityDetail, setFacilityDetail] = useState<IFacilityDetail>({
    isLeased: false,
    country: 0,
    type: 0,
    label: '',
    value: ''
  });

  useEffect(() => {
    Promise.all([
      facilityDetailsDropdown(APITypeEnum.GET_ALL, true),
      efdbDatabaseDropdown(),
      designation(),
      facilityType(),
      country()
    ])
      .then(([facilityDetails, efdbDatabaseResponse]) => {
        const facilityYears = sortYearOptions(
          yearFilterArray(facilityDetails, undefined)
        );
        const firstYearMain = facilityYears?.[0]?.value;
        const searchYear = searchParam.get('selectedYear') ?? '';
        const hasYear = facilityYears?.some((i: any) => i.value === searchYear);
        const setYear = hasYear ? searchYear : firstYearMain;
        searchParam.set('selectedYear', setYear);
        setSelectedYear(setYear);

        const hasFacility = facilityDetails?.[setYear]?.some((i: any) => {
          return i?._id?.toString() === searchParam.get('facility');
        });
        hasFacility
          ? searchParam.set('facility', searchParam.get('facility') ?? '')
          : searchParam.delete('facility');

        setFacilityId(hasFacility ? searchParam.get('facility') ?? '' : '');
        setSearchParams(searchParam);
        setEfdbData(efdbDatabaseResponse);
      })
      .catch((err) => {
        console.error('Error occurred:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const filterFacilityDetails = useMemo(() => {
    if (facilities?.[selectedYear]) {
      return facilities?.[selectedYear];
    } else {
      return [];
    }
  }, [selectedYear, facilities]);

  useEffect(() => {
    setFacilityDetail(
      filterFacilityDetails
        .filter((item: any) => {
          return item?._id === facilityId;
        })
        .map((item: any) => {
          return {
            isLeased: item.isLeased,
            country: item.country,
            type: item.type,
            label: item.name,
            value: item._id
          };
        })?.[0]
    );
  }, [filterFacilityDetails, facilityId]);

  useEffect(() => {
    setSelectedCountry(`${facilityDetail?.country ?? ''}`);
  }, [facilityDetail]);

  return isLoading ? (
    <Loader />
  ) : (
    selectedYear !== '' ? (
      <>
        <FacilitySelect
          setFacilityId={setFacilityId}
          setSelectedYear={setSelectedYear}
          selectedYear={selectedYear}
          showLeased={false}
          isConfigurator={'employee'}
        />
        <Divider />
        <SelectedCountryContext.Provider
          value={{
            selectedCountry,
            setSelectedCountry,
            efdbData,
            facilityId,
            selectedYear
          }}
        >
          <EmpTable
            selectedYear={selectedYear}
            facilityId={facilityId}
            isConfigurator={true}
          />
        </SelectedCountryContext.Provider>
      </>
    ) : null
  );
};

export default EmployeeDetails;
